import dayjs from 'dayjs'

import { getI18n } from '@/plugins/i18n'
import { scheduleApi } from '@/apis/schedule.api'
import { usePlanningStore } from '@/stores/planning.store'
import { useAllocationConfirm } from '@/composables/allocation-confirm'
import { useNotify } from '@/composables/notify'

export const useStaffMovements = () => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const { notifySuccess } = useNotify()
  const { noStockDialog, runConfirmAsync } = useAllocationConfirm()

  // Daily event slots collapse into one row per assignment. The budget row is the
  // assignment's source of truth, but a group row is shared by every descendant
  // service, so the staff member is part of the key too.
  const groupAllocations = (events) => {
    const groups = new Map()
    for (const event of events) {
      const key = `${event.budget_document_row_id}-${event.service_id}`
      const list = groups.get(key) ?? []
      list.push(event)
      groups.set(key, list)
    }

    return [...groups.entries()].map(([key, groupEvents]) => {
      const project = planningStore.getProjectById(groupEvents[0].project_id)
      return {
        key,
        projectId: groupEvents[0].project_id,
        serviceId: groupEvents[0].service_id,
        budgetDocumentRowId: groupEvents[0].budget_document_row_id,
        name: project?.name ?? i18n.t('common.project'),
        color: project?.color,
        staffName: planningStore.getServiceName(planningStore.getServiceById(groupEvents[0].service_id)),
        start: groupEvents.reduce((min, e) => (dayjs(e.start).isBefore(min) ? e.start : min), groupEvents[0].start),
        end: groupEvents.reduce((max, e) => (dayjs(e.end).isAfter(max) ? e.end : max), groupEvents[0].end),
        confirmed: groupEvents.every(e => e.confirmed),
      }
    })
  }

  // Staff never needs a warehouse choice, so confirmation auto-resolves and only the
  // insufficient-availability warning can interrupt it.
  const approveAllocationAsync = (group, onDone) => runConfirmAsync(
    force => scheduleApi.confirmAllocationAsync(
      group.serviceId, group.projectId, [], group.budgetDocumentRowId, force, { skipErrorMessage: true },
    ),
    async() => {
      notifySuccess(i18n.t('info.updatedSuccessfully'))
      await onDone?.()
    },
  )

  const revertAllocationAsync = async(group, onDone) => {
    await scheduleApi.cancelAllocationAsync(group.serviceId, group.projectId, group.budgetDocumentRowId)
    notifySuccess(i18n.t('info.updatedSuccessfully'))
    await onDone?.()
  }

  return { groupAllocations, noStockDialog, approveAllocationAsync, revertAllocationAsync }
}
